import { getBundleColor } from 'pages/oldPlanner/utils/bundleDraft';
import { PlannerOrder } from 'pages/planner/utils/usePlannerData';
import { readableColor } from 'polished';
import React, { useRef, useState } from 'react';
import { FaUserCheck } from 'react-icons/fa';
import { BundleJson } from 'types/bundleTypes';
import './MapPopup.scss';

interface OrderPopupProps {
  title: string;
  orders: PlannerOrder[];
  selectedOrderIds: string[];
  windowGroups: Map<string, string>;
  fetchedBundles: BundleJson[];
  callback: (order: PlannerOrder[]) => void;
}

enum Action {
  Check = 1,
  Uncheck = 0,
}

export const OrderPopup = ({
  title,
  orders,
  selectedOrderIds,
  windowGroups,
  fetchedBundles,
  callback,
  ...props
}: OrderPopupProps) => {
  const [toggleReload, setToggleReload] = useState<boolean>(false);

  const clickedSelectedOrders = useRef<string[]>([]);

  const savedSelectedOrders = useRef<string[]>(selectedOrderIds);

  /* This is needed because the useEffect is not working with the ReactDOM */
  const sendUnsavedSelectedOrders = (order: PlannerOrder, action: Action) => {
    /* Only use Callback one time */
    if (action === Action.Check) {
      clickedSelectedOrders.current.forEach(clickedOrder => {
        const hasBeenSavedPreviously = savedSelectedOrders.current.some(
          savedOrder => savedOrder === clickedOrder
        );
        if (!hasBeenSavedPreviously) {
          callback([order]);
          savedSelectedOrders.current.push(order.id);
        }
      });
    } else {
      /* Check if exist in saved */
      const savedOrderIndex = savedSelectedOrders.current.findIndex(
        savedOrder => savedOrder === order.id
      );
      if (savedOrderIndex > -1) {
        savedSelectedOrders.current.splice(savedOrderIndex, 1);
        callback([order]);
      }
    }
    clickedSelectedOrders.current = [];
  };

  const groupedOrders = new Map<
    string,
    {
      id: string;
      courier: string;
      orders: PlannerOrder[];
    }
  >();
  orders.map(order => {
    const oldBundle = groupedOrders.get(order.bundleId);

    let ordersTmp: PlannerOrder[];
    let courier: string = oldBundle?.courier;

    if (oldBundle) {
      ordersTmp = [...oldBundle.orders, order];
    } else {
      const bundle = fetchedBundles.find(x => order.bundleId === x.id);
      courier = bundle?.assignedCourierName;
      ordersTmp = [order];
    }

    groupedOrders.set(order.bundleId, {
      id: order.id,
      courier,
      orders: ordersTmp,
    });
  });

  return (
    <>
      {title && (
        <h6>
          <strong>{title}</strong>
        </h6>
      )}

      {[...groupedOrders].map(([bundleId, groupedOrder]) => (
        <div key={`bundle-popup-${bundleId}`} className="map-popup__wrapper">
          <h6
            className="map-popup__title"
            style={
              bundleId && {
                backgroundColor: getBundleColor(bundleId),
                color: readableColor(
                  getBundleColor(bundleId),
                  '#000000',
                  '#ffffff'
                ),
              }
            }
          >
            {bundleId ? <>Bundle {bundleId}</> : <>Order without Bundle :</>}
          </h6>
          {groupedOrder.courier ? (
            <div className="map-popup__courier">
              <FaUserCheck /> <span>{groupedOrder.courier}</span>
            </div>
          ) : (
            <div className="map-popup__courier">No courier assigned</div>
          )}
          <ul>
            {groupedOrder.orders.map(order => {
              const checked = savedSelectedOrders.current.includes(order.id);

              return (
                <li
                  key={`bundle-popup-${bundleId}-order${order.id}`}
                  className="map-popup"
                >
                  {bundleId ? (
                    <a
                      className="map-popup__bundle"
                      href={`/bundles/${bundleId}`}
                      style={{
                        backgroundColor: getBundleColor(bundleId),
                        color: readableColor(
                          getBundleColor(bundleId),
                          '#000000',
                          '#ffffff'
                        ),
                      }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      B
                    </a>
                  ) : (
                    <div className="map-popup__bundle">B</div>
                  )}
                  <input
                    type="checkbox"
                    onChange={() => {
                      clickedSelectedOrders.current.push(order.id);
                      sendUnsavedSelectedOrders(
                        order,
                        checked ? Action.Uncheck : Action.Check
                      );
                      /* This setState is only used for component reload */
                      setToggleReload(!toggleReload);
                    }}
                    checked={checked}
                  />
                  <a href={`/orders/${order.id}`}>{order.id}</a>
                  <div style={{ width: 8 }} />
                  <div
                    className="map-popup__window"
                    style={{ backgroundColor: windowGroups.get(order.window) }}
                  >
                    {order.window}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </>
  );
};
